import axios, { 
  AxiosInstance, 
  AxiosRequestConfig, 
  AxiosResponse, 
  AxiosError,
  InternalAxiosRequestConfig 
} from 'axios';
import { ApiResponse, ApiError } from '@/types/api.types';

// API base configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/';
const REQUEST_TIMEOUT = 30000;

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false, // Set to true if backend requires cookies
});

// Token management utilities
export const tokenManager = {
  getAccessToken: (): string | null => {
    return sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
  },

  setAccessToken: (token: string, rememberMe: boolean = false): void => {
    if (rememberMe) {
      localStorage.setItem('access_token', token);
    } else {
      sessionStorage.setItem('access_token', token);
    }
  },

  getRefreshToken: (): string | null => {
    return sessionStorage.getItem('refresh_token') || localStorage.getItem('refresh_token');
  },

  setRefreshToken: (token: string, rememberMe: boolean = false): void => {
    if (rememberMe) {
      localStorage.setItem('refresh_token', token);
    } else {
      sessionStorage.setItem('refresh_token', token);
    }
  },

  clearTokens: (): void => {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('token_expiry');
    sessionStorage.removeItem('current_user');

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('current_user');
  },

  getTokenExpiry: (): number | null => {
    const expiry = sessionStorage.getItem('token_expiry') || localStorage.getItem('token_expiry');
    return expiry ? parseInt(expiry) : null;
  },

  setTokenExpiry: (expiresIn: number, rememberMe: boolean = false): void => {
    if (!expiresIn || isNaN(expiresIn)) {
      expiresIn = 3600; // Default to 1 hour if invalid
    }
    const expiry = Date.now() + (expiresIn * 1000);
    if (rememberMe) {
      localStorage.setItem('token_expiry', expiry.toString());
    } else {
      sessionStorage.setItem('token_expiry', expiry.toString());
    }
  },
  
  isTokenExpired: (): boolean => {
    const expiry = tokenManager.getTokenExpiry();
    if (!expiry) return true;
    
    // Check if token expires in the next 5 minutes
    return (expiry - Date.now()) < (5 * 60 * 1000);
  }
};

// Request interceptor for JWT
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log POST requests for debugging
    if (config.method === 'post' && config.url?.includes('/patients')) {
      console.log('Patient API Request:', {
        url: config.url,
        headers: {
          Authorization: config.headers.Authorization ? 'Bearer [TOKEN]' : 'Missing',
          ContentType: config.headers['Content-Type']
        },
        data: config.data
      });
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh and error handling
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean, _isLoginRequest?: boolean };

    // Mark login requests to avoid redirect loops
    if (originalRequest.url?.includes('/auth/login')) {
      originalRequest._isLoginRequest = true;
    }

    // Handle 401 errors (unauthorized) - but not for login requests
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest._isLoginRequest) {
      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenManager.getRefreshToken();
      
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken
          });

          const { access_token, expires_in } = response.data.data;
          
          tokenManager.setAccessToken(access_token);
          tokenManager.setTokenExpiry(expires_in);

          processQueue(null, access_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          
          return apiClient(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          tokenManager.clearTokens();
          
          // Don't redirect if we're already on the login page or if this is a login request
          if (!window.location.pathname.includes('login') && !originalRequest._isLoginRequest) {
            // Use React Router navigation instead of direct href change
            window.location.href = '/';
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        tokenManager.clearTokens();
        // Don't redirect if we're already on the login page or if this is a login request
        if (!window.location.pathname.includes('login') && !originalRequest._isLoginRequest) {
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// API wrapper with Romanian error handling
export const api = {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error) {
      throw transformError(error as AxiosError);
    }
  },

  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error) {
      throw transformError(error as AxiosError);
    }
  },

  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw transformError(error as AxiosError);
    }
  },

  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      throw transformError(error as AxiosError);
    }
  }
};

// Transform axios errors to Romanian API errors
function transformError(error: AxiosError): ApiError {
  if (error.response?.data) {
    const responseData = error.response.data as any;
    return {
      message: responseData.error || 'Eroare necunoscută',
      code: responseData.code,
      status: error.response.status,
      details: responseData.details,
    };
  }

  if (error.code === 'ECONNABORTED') {
    return {
      message: 'Cererea a expirat. Vă rugăm să încercați din nou.',
      code: 'TIMEOUT'
    };
  }

  if (error.code === 'ERR_NETWORK') {
    return {
      message: 'Eroare de rețea. Verificați conexiunea la internet.',
      code: 'NETWORK_ERROR'
    };
  }

  return {
    message: 'Eroare necunoscută. Vă rugăm să încercați din nou.',
    code: 'UNKNOWN_ERROR'
  };
}

export default apiClient;
